/**
 * Notificaciones Masivas — Centro de comunicación
 * Sub-componente embebido en AdminSuscripciones.
 *
 * Permite al admin enviar campañas (email + campanita + push) combinando
 * filtros de nivel, destinatario, país, provincia, forma de pago y actividad.
 * Incluye preview, historial y gestión de talleres excluidos.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Send, Eye, History, Mail, Bell, Smartphone, Loader2,
  CheckCircle2, AlertCircle, BellOff, Users, RefreshCw,
} from 'lucide-react';
import { notificacionesMasivasAPI, paisesAPI } from '../../services/api';

const NIVELES_OPTIONS = [
  { value: 'premium', label: 'Premium' },
  { value: 'estandar_pro', label: 'Standard Pro' },
  { value: 'estandar', label: 'Standard' },
  { value: 'status_report', label: 'Status Report' },
  { value: 'sin_suscripcion', label: 'Sin Suscripción' },
];

const FORMAS_PAGO_OPTIONS = [
  { value: 'mensual_manual', label: 'Mensual manual' },
  { value: 'debito_automatico', label: 'Débito automático' },
  { value: 'anual', label: 'Anual (próximamente)' },
];

const DESTINATARIO_OPTIONS = [
  { value: 'titular_y_oficial', label: 'Titular + Email oficial' },
  { value: 'usuarios_comunes', label: 'Solo usuarios comunes (no Titular)' },
  { value: 'todos', label: 'Todos los usuarios + Email oficial' },
];

const formatDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('es-AR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const EstadoBadge = ({ estado }) => {
  if (estado === 'completada') {
    return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="h-3 w-3 mr-1" />Completada</Badge>;
  }
  if (estado === 'en_curso') {
    return <Badge className="bg-amber-100 text-amber-700"><Loader2 className="h-3 w-3 mr-1 animate-spin" />En curso</Badge>;
  }
  if (estado === 'fallida') {
    return <Badge variant="destructive"><AlertCircle className="h-3 w-3 mr-1" />Fallida</Badge>;
  }
  return <Badge variant="outline">{estado}</Badge>;
};

const NotificacionesMasivas = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Modo "envío directo a 1 taller" — viene desde el botón "Enviar mensaje" en otras vistas
  const [tallerIdsForzados, setTallerIdsForzados] = useState([]);
  const [tallerNombreForzado, setTallerNombreForzado] = useState('');

  // Form state
  const [asunto, setAsunto] = useState('');
  const [cuerpoHtml, setCuerpoHtml] = useState('');
  const [canales, setCanales] = useState({ email: true, campanita: true, push: false });
  const [niveles, setNiveles] = useState([]);
  const [destinatario, setDestinatario] = useState('titular_y_oficial');
  const [paisesSeleccionados, setPaisesSeleccionados] = useState([]);
  const [provinciasSeleccionadas, setProvinciasSeleccionadas] = useState([]);
  const [formasPago, setFormasPago] = useState([]);
  const [soloInactivos, setSoloInactivos] = useState(false);
  const [bccBiblioteca, setBccBiblioteca] = useState(true);

  // Data
  const [paises, setPaises] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [excluidos, setExcluidos] = useState([]);

  // Preview + envío
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [showConfirmar, setShowConfirmar] = useState(false);
  const [detalleAbierta, setDetalleAbierta] = useState(null);

  useEffect(() => {
    paisesAPI.getActivos().then(r => setPaises(r.data?.items || [])).catch(() => setPaises([]));
    cargarHistorial();
    cargarExcluidos();
  }, []);

  // Leer ?taller_ids=ID y ?taller_nombre=Razón (desde "Enviar mensaje" en otras vistas)
  useEffect(() => {
    const ids = searchParams.get('taller_ids');
    const nombre = searchParams.get('taller_nombre');
    if (ids) {
      setTallerIdsForzados(ids.split(',').filter(Boolean));
      setTallerNombreForzado(nombre || '');
      // Limpiar URL
      const np = new URLSearchParams(searchParams);
      np.delete('taller_ids');
      np.delete('taller_nombre');
      setSearchParams(np, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const cargarHistorial = async () => {
    try {
      const r = await notificacionesMasivasAPI.getHistorial(30);
      setHistorial(r.data || []);
    } catch {
      setHistorial([]);
    }
  };
  const cargarExcluidos = async () => {
    try {
      const r = await notificacionesMasivasAPI.getExcluidos();
      setExcluidos(r.data || []);
    } catch {
      setExcluidos([]);
    }
  };

  const provinciasDisponibles = useMemo(() => {
    if (paisesSeleccionados.length === 0) {
      // Si no hay país seleccionado, mostrar provincias de TODOS los países activos
      return paises.flatMap(p => (p.provincias || []).map(prov => ({ pais: p.nombre, nombre: prov })));
    }
    return paises
      .filter(p => paisesSeleccionados.includes(p.nombre))
      .flatMap(p => (p.provincias || []).map(prov => ({ pais: p.nombre, nombre: prov })));
  }, [paises, paisesSeleccionados]);

  const buildPayload = () => ({
    asunto: asunto.trim(),
    cuerpo_html: cuerpoHtml.trim(),
    canales: Object.entries(canales).filter(([, v]) => v).map(([k]) => k),
    filtros: {
      niveles: tallerIdsForzados.length ? [] : niveles,
      destinatario,
      paises: tallerIdsForzados.length ? [] : paisesSeleccionados,
      provincias: tallerIdsForzados.length ? [] : provinciasSeleccionadas,
      formas_pago: tallerIdsForzados.length ? [] : formasPago,
      solo_inactivos_30d: tallerIdsForzados.length ? false : soloInactivos,
      taller_ids: tallerIdsForzados,
    },
    bcc_biblioteca: bccBiblioteca,
  });

  const limpiarModoDirecto = () => {
    setTallerIdsForzados([]);
    setTallerNombreForzado('');
  };

  const handlePreview = async () => {
    if (!asunto.trim() || !cuerpoHtml.trim()) {
      toast.error('Completá el asunto y el cuerpo del mensaje');
      return;
    }
    const canalesArr = Object.entries(canales).filter(([, v]) => v).map(([k]) => k);
    if (canalesArr.length === 0) {
      toast.error('Elegí al menos un canal (email / campanita / push)');
      return;
    }
    setLoadingPreview(true);
    setPreview(null);
    try {
      const r = await notificacionesMasivasAPI.preview(buildPayload());
      setPreview(r.data);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Error al calcular la vista previa');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleEnviar = async () => {
    setEnviando(true);
    try {
      const r = await notificacionesMasivasAPI.enviar(buildPayload());
      toast.success(`Campaña encolada · ${r.data?.alcance_estimado?.total_talleres || 0} talleres`);
      setShowConfirmar(false);
      // Resetear form parcialmente (mantener filtros, limpiar contenido)
      setAsunto('');
      setCuerpoHtml('');
      setPreview(null);
      // Refrescar historial luego de unos segundos para ver el resultado
      setTimeout(cargarHistorial, 3000);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Error al enviar la campaña');
    } finally {
      setEnviando(false);
    }
  };

  const toggleExclusion = async (tallerId, valor) => {
    try {
      await notificacionesMasivasAPI.toggleExclusion(tallerId, valor);
      toast.success(valor ? 'Taller excluido' : 'Taller reincluido');
      cargarExcluidos();
    } catch (e) {
      toast.error('Error al cambiar la exclusión');
    }
  };

  const verDetalle = async (campaniaId) => {
    try {
      const r = await notificacionesMasivasAPI.getDetalle(campaniaId);
      setDetalleAbierta(r.data);
    } catch {
      toast.error('Error al cargar detalle');
    }
  };

  const toggleArrayValue = (arr, setArr, value) => {
    if (arr.includes(value)) setArr(arr.filter(v => v !== value));
    else setArr([...arr, value]);
  };

  return (
    <div className="space-y-6" data-testid="notif-masivas-tab">
      {/* Modo "envío directo a 1 taller" */}
      {tallerIdsForzados.length > 0 && (
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg p-4 flex items-center justify-between" data-testid="notif-modo-directo-banner">
          <div>
            <p className="font-semibold flex items-center gap-2">
              <Send className="h-4 w-4" /> Envío directo a 1 taller
            </p>
            <p className="text-sm text-purple-50 mt-1">
              {tallerNombreForzado || `Taller ${tallerIdsForzados[0].slice(0, 8)}…`} — los filtros generales están desactivados.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={limpiarModoDirecto}
            className="bg-white/20 border-white/40 text-white hover:bg-white/30"
            data-testid="notif-modo-directo-quitar"
          >
            Volver a modo masivo
          </Button>
        </div>
      )}

      {/* Editor de campaña */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-teal-600" />
            Nueva campaña
          </CardTitle>
          <CardDescription>
            Combiná filtros y canales para enviar a un segmento específico. La campaña se ejecuta en segundo plano.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Asunto */}
          <div className="space-y-2">
            <Label>Asunto del mensaje *</Label>
            <Input
              value={asunto}
              onChange={(e) => setAsunto(e.target.value)}
              placeholder="Ej: Nueva revisión publicada — Boeing 737"
              maxLength={200}
              data-testid="notif-masivas-asunto"
            />
          </div>

          {/* Cuerpo */}
          <div className="space-y-2">
            <Label>Cuerpo del mensaje (HTML simple permitido: &lt;b&gt;, &lt;i&gt;, &lt;a&gt;, &lt;br&gt;) *</Label>
            <Textarea
              value={cuerpoHtml}
              onChange={(e) => setCuerpoHtml(e.target.value)}
              placeholder="<p>Hola,</p><p>Queríamos avisarte que...</p>"
              rows={8}
              className="font-mono text-sm"
              data-testid="notif-masivas-cuerpo"
            />
            <p className="text-xs text-gray-500">
              Se va a envolver en el template institucional con logo, header teal y footer. Si solo escribís texto, también funciona.
            </p>
          </div>

          {/* Canales */}
          <div className="space-y-2">
            <Label>Canales de envío *</Label>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={canales.email}
                  onCheckedChange={(v) => setCanales({ ...canales, email: !!v })}
                  data-testid="notif-canal-email"
                />
                <Mail className="h-4 w-4 text-blue-600" /> Email
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={canales.campanita}
                  onCheckedChange={(v) => setCanales({ ...canales, campanita: !!v })}
                  data-testid="notif-canal-campanita"
                />
                <Bell className="h-4 w-4 text-amber-600" /> Campanita
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={canales.push}
                  onCheckedChange={(v) => setCanales({ ...canales, push: !!v })}
                  data-testid="notif-canal-push"
                />
                <Smartphone className="h-4 w-4 text-purple-600" /> Push
              </label>
            </div>
          </div>

          {/* Filtros */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-3 border-t">
            <div className="space-y-2">
              <Label>Niveles de suscripción (vacío = todos)</Label>
              <div className="flex flex-wrap gap-2">
                {NIVELES_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggleArrayValue(niveles, setNiveles, opt.value)}
                    className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                      niveles.includes(opt.value)
                        ? 'bg-teal-600 text-white border-teal-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                    data-testid={`notif-nivel-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Destinatario</Label>
              <Select value={destinatario} onValueChange={setDestinatario}>
                <SelectTrigger data-testid="notif-destinatario">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DESTINATARIO_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Países (vacío = todos)</Label>
              <div className="flex flex-wrap gap-2">
                {paises.map(p => (
                  <button
                    key={p.nombre}
                    type="button"
                    onClick={() => {
                      toggleArrayValue(paisesSeleccionados, setPaisesSeleccionados, p.nombre);
                      setProvinciasSeleccionadas([]);
                    }}
                    className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                      paisesSeleccionados.includes(p.nombre)
                        ? 'bg-teal-600 text-white border-teal-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {p.bandera || ''} {p.nombre}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Provincias (vacío = todas)</Label>
              {provinciasDisponibles.length === 0 ? (
                <p className="text-xs text-gray-500 italic">Seleccioná un país primero o dejá vacío.</p>
              ) : (
                <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto p-2 border rounded">
                  {provinciasDisponibles.map((prov, i) => (
                    <button
                      key={`${prov.pais}-${prov.nombre}-${i}`}
                      type="button"
                      onClick={() => toggleArrayValue(provinciasSeleccionadas, setProvinciasSeleccionadas, prov.nombre)}
                      className={`px-2 py-0.5 rounded text-xs border ${
                        provinciasSeleccionadas.includes(prov.nombre)
                          ? 'bg-teal-600 text-white border-teal-600'
                          : 'bg-white text-gray-600 border-gray-300'
                      }`}
                    >
                      {prov.nombre}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Forma de pago (vacío = todas)</Label>
              <div className="flex flex-wrap gap-2">
                {FORMAS_PAGO_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggleArrayValue(formasPago, setFormasPago, opt.value)}
                    className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                      formasPago.includes(opt.value)
                        ? 'bg-teal-600 text-white border-teal-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                    data-testid={`notif-formapago-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Actividad</Label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch
                  checked={soloInactivos}
                  onCheckedChange={setSoloInactivos}
                  data-testid="notif-inactivos-switch"
                />
                <span className="text-sm">Solo talleres sin actividad en los últimos 30 días</span>
              </label>
            </div>
          </div>

          {/* BCC */}
          <div className="flex items-center gap-2 pt-3 border-t">
            <Switch
              checked={bccBiblioteca}
              onCheckedChange={setBccBiblioteca}
              data-testid="notif-bcc-switch"
            />
            <Label className="cursor-pointer">
              Enviar BCC silencioso a <code className="text-xs bg-gray-100 px-1 rounded">biblioteca@cmpublicaciones.com</code>
            </Label>
          </div>

          {/* Acciones */}
          <div className="flex flex-wrap gap-2 pt-3 border-t">
            <Button
              variant="outline"
              onClick={handlePreview}
              disabled={loadingPreview}
              data-testid="notif-preview-btn"
            >
              {loadingPreview ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
              Vista previa
            </Button>
            <Button
              onClick={() => preview && setShowConfirmar(true)}
              disabled={!preview || preview.total_talleres === 0}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="notif-enviar-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              Enviar campaña
            </Button>
          </div>

          {/* Preview result */}
          {preview && (
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 space-y-2" data-testid="notif-preview-result">
              <p className="font-medium text-teal-900 flex items-center gap-2">
                <Users className="h-4 w-4" /> Alcance estimado
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-teal-700">Talleres</p>
                  <p className="text-2xl font-bold text-teal-900">{preview.total_talleres}</p>
                </div>
                <div>
                  <p className="text-blue-700">Emails</p>
                  <p className="text-2xl font-bold text-blue-900">{preview.total_emails}</p>
                </div>
                <div>
                  <p className="text-amber-700">Campanitas</p>
                  <p className="text-2xl font-bold text-amber-900">{preview.total_campanitas}</p>
                </div>
                <div>
                  <p className="text-purple-700">Push</p>
                  <p className="text-2xl font-bold text-purple-900">{preview.total_push}</p>
                </div>
              </div>
              {preview.total_talleres === 0 && (
                <p className="text-sm text-red-600 italic">No hay talleres que cumplan estos filtros.</p>
              )}
              {preview.bcc_biblioteca && (
                <p className="text-xs text-gray-500 italic">+ 1 BCC silencioso a biblioteca@cmpublicaciones.com</p>
              )}

              {/* Lista de talleres alcanzados con opción de excluir individual */}
              {preview.talleres_alcanzados && preview.talleres_alcanzados.length > 0 && (
                <div className="mt-3 pt-3 border-t border-teal-200" data-testid="notif-preview-lista-talleres">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-teal-900">
                      Talleres alcanzados {preview.talleres_alcanzados.length < preview.total_talleres && (
                        <span className="text-xs text-teal-700 font-normal">(mostrando {preview.talleres_alcanzados.length} de {preview.total_talleres})</span>
                      )}
                    </p>
                    <p className="text-xs text-teal-700 italic">Tocá "Excluir" para sacar uno antes de enviar</p>
                  </div>
                  <div className="max-h-64 overflow-y-auto bg-white rounded border border-teal-200 divide-y">
                    {preview.talleres_alcanzados.map(t => (
                      <div
                        key={t.id}
                        className="flex items-center justify-between px-3 py-2 hover:bg-teal-50"
                        data-testid={`notif-preview-taller-${t.id}`}
                      >
                        <span className="text-sm text-gray-800 truncate flex-1 pr-2">{t.razon_social}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                          onClick={async () => {
                            try {
                              await notificacionesMasivasAPI.toggleExclusion(t.id, true);
                              toast.success(`"${t.razon_social}" excluido`);
                              // Recargar preview + lista global de excluidos
                              await handlePreview();
                              cargarExcluidos();
                            } catch {
                              toast.error('Error al excluir el taller');
                            }
                          }}
                          data-testid={`notif-preview-excluir-${t.id}`}
                          title="Excluir este taller del envío (queda excluido también para futuras campañas)"
                        >
                          <BellOff className="h-3 w-3 mr-1" />
                          Excluir
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Historial */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5 text-slate-600" />
                Historial de campañas
              </CardTitle>
              <CardDescription>Últimas 30 campañas enviadas</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={cargarHistorial}>
              <RefreshCw className="h-4 w-4 mr-2" />Refrescar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {historial.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No hay campañas todavía.</p>
          ) : (
            <div className="space-y-2">
              {historial.map(c => (
                <div
                  key={c.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => verDetalle(c.id)}
                  data-testid={`notif-historial-row-${c.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{c.asunto}</p>
                    <p className="text-xs text-gray-500">
                      {formatDate(c.created_at)} · por {c.creada_por?.email || '-'}
                    </p>
                    <div className="flex gap-2 mt-1">
                      {(c.canales || []).map(can => (
                        <Badge key={can} variant="outline" className="text-xs">{can}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-3 space-y-1">
                    <EstadoBadge estado={c.estado} />
                    <p className="text-xs text-gray-500">
                      {c.resultados
                        ? `${c.resultados.emails_enviados || 0} emails · ${c.resultados.campanitas_creadas || 0} bell`
                        : `~${c.alcance_estimado?.total_talleres || 0} talleres`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Talleres excluidos */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BellOff className="h-5 w-5 text-red-600" />
            Talleres excluidos
          </CardTitle>
          <CardDescription>
            Estos talleres NO recibirán ninguna campaña masiva (sí siguen recibiendo emails transaccionales y de suscripción).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {excluidos.length === 0 ? (
            <p className="text-sm text-gray-500 italic">Ningún taller está excluido.</p>
          ) : (
            <div className="space-y-1">
              {excluidos.map(t => (
                <div key={t.id} className="flex items-center justify-between p-2 border rounded">
                  <div>
                    <p className="font-medium text-sm">{t.razon_social}</p>
                    <p className="text-xs text-gray-500">
                      {t.numero_anac} · {t.nivel_suscripcion || 'sin_nivel'}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleExclusion(t.id, false)}
                    data-testid={`notif-excluido-quitar-${t.id}`}
                  >
                    Quitar exclusión
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog: Confirmar envío */}
      <Dialog open={showConfirmar} onOpenChange={setShowConfirmar}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar envío de campaña</DialogTitle>
            <DialogDescription>
              Estás por enviar <strong>"{asunto}"</strong> a:
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-2 py-2">
              <p className="text-sm">
                · <strong>{preview.total_talleres}</strong> talleres
              </p>
              {canales.email && <p className="text-sm">· <strong>{preview.total_emails}</strong> emails</p>}
              {canales.campanita && <p className="text-sm">· <strong>{preview.total_campanitas}</strong> campanitas</p>}
              {canales.push && <p className="text-sm">· <strong>{preview.total_push}</strong> push</p>}
              {bccBiblioteca && canales.email && (
                <p className="text-xs text-gray-500 italic">+ BCC a biblioteca@cmpublicaciones.com</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmar(false)} disabled={enviando}>
              Cancelar
            </Button>
            <Button
              onClick={handleEnviar}
              disabled={enviando}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="notif-enviar-confirmar"
            >
              {enviando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Sí, enviar ahora
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Detalle de campaña */}
      <Dialog open={!!detalleAbierta} onOpenChange={(v) => !v && setDetalleAbierta(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{detalleAbierta?.asunto}</DialogTitle>
            <DialogDescription>
              {formatDate(detalleAbierta?.created_at)} · por {detalleAbierta?.creada_por?.email}
            </DialogDescription>
          </DialogHeader>
          {detalleAbierta && (
            <div className="space-y-3 py-2 text-sm">
              <div>
                <EstadoBadge estado={detalleAbierta.estado} />
              </div>
              <div>
                <p className="font-medium text-gray-700 mb-1">Canales</p>
                <div className="flex gap-2">
                  {detalleAbierta.canales?.map(c => <Badge key={c} variant="outline">{c}</Badge>)}
                </div>
              </div>
              <div>
                <p className="font-medium text-gray-700 mb-1">Filtros aplicados</p>
                <pre className="bg-gray-50 p-2 rounded text-xs overflow-x-auto">
                  {JSON.stringify(detalleAbierta.filtros, null, 2)}
                </pre>
              </div>
              <div>
                <p className="font-medium text-gray-700 mb-1">Cuerpo</p>
                <div
                  className="border rounded p-3 bg-white max-h-64 overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: detalleAbierta.cuerpo_html || '' }}
                />
              </div>
              {detalleAbierta.resultados && (
                <div>
                  <p className="font-medium text-gray-700 mb-1">Resultados</p>
                  <pre className="bg-gray-50 p-2 rounded text-xs overflow-x-auto">
                    {JSON.stringify(detalleAbierta.resultados, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificacionesMasivas;
